import { useState } from "react";
import { Bell, CheckCheck, X } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dismissAlerts, getAdminAlerts, markAlertsRead } from "@/api/admin";
import type { AdminAlert, AdminAlertSeverity } from "@/types/admin";
import { useAuthStore } from "@/store/auth";
import { useAdminBasePath } from "@/contexts/AdminBasePathContext";
import { rewriteAlertLink } from "@/features/admin/components/alertLink";
import { alertTypeMeta } from "@/features/admin/lib/alertType";
import { cn } from "@/lib/cn";
import { formatRelativeTime } from "@/lib/formatRelativeTime";
import { toastError, toastSuccess } from "@/lib/toast";
import { toneClasses } from "@/components/ui/badgeTones";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";

/** Root alerts query key — also the badge source, polled every 60s. */
const ALERTS_QUERY_KEY = ["admin", "alerts"] as const;

/** Tailwind classes for the small severity dot, keyed by alert severity. */
const SEVERITY_DOT: Record<AdminAlertSeverity, string> = {
  info: "bg-blue-500",
  warning: "bg-amber-500",
  critical: "bg-red-500",
};

/** Tailwind classes for the severity label pill, keyed by alert severity. */
const SEVERITY_PILL: Record<AdminAlertSeverity, string> = {
  // Badge tones (TASK-5133, DEV_RULES §10.10).
  info: toneClasses("info"),
  warning: toneClasses("warning"),
  critical: toneClasses("danger"),
};

/**
 * AdminShell notification bell (TASK-3620, TASK-3676).
 *
 * Polls `GET /admin/alerts/` every 60s and renders a badge with the unread
 * count plus a Radix dropdown listing the current superadmin/dealer system
 * alerts (overdue invoices, suspended tenants, optimization failures). Each
 * alert with a `link` is navigable (rewritten to the active shell's base path);
 * each row carries a dismiss control. The header exposes "Mark all read" and the
 * footer a "View all" link to the alerts inbox page. Opening the dropdown still
 * marks the visible alerts read (TASK-3620 behavior retained).
 *
 * Loading and error states are handled gracefully so the shell never crashes:
 * a failed fetch simply shows the empty state and hides the badge.
 *
 * Robustness (TASK-3663): the query is gated on the authenticated user being
 * loaded so it never fires during the brief post-login hydration window.
 * Retries are bounded so a transient 5xx blip cannot turn into a retry storm,
 * and a failed fetch is swallowed (no console spam) — the bell simply shows no
 * alerts.
 */
export function AlertsBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const basePath = useAdminBasePath();
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ALERTS_QUERY_KEY,
    queryFn: () => getAdminAlerts(),
    enabled: Boolean(user),
    refetchInterval: 60_000,
    // Cap retries: avoid hammering the endpoint during a transient outage.
    retry: 2,
  });

  const markReadMutation = useMutation({
    mutationFn: markAlertsRead,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEY });
    },
  });

  // "Mark all read" — optimistically zero the badge, roll back on error.
  const markAllReadMutation = useMutation({
    mutationFn: () => markAlertsRead({ all: true }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ALERTS_QUERY_KEY });
      const previous = queryClient.getQueryData<typeof data>(ALERTS_QUERY_KEY);
      if (previous) {
        queryClient.setQueryData(ALERTS_QUERY_KEY, {
          ...previous,
          unread_count: 0,
          results: previous.results.map((alert) => ({
            ...alert,
            is_read: true,
          })),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(ALERTS_QUERY_KEY, context.previous);
      }
      toastError(t("adminPanel.alerts.markReadError"));
    },
    onSuccess: () => {
      toastSuccess(t("adminPanel.alerts.markReadSuccess"));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEY });
    },
  });

  // Per-row dismiss — optimistically remove the row, roll back on error.
  const dismissMutation = useMutation({
    mutationFn: (key: string) => dismissAlerts({ keys: [key] }),
    onMutate: async (key: string) => {
      await queryClient.cancelQueries({ queryKey: ALERTS_QUERY_KEY });
      const previous = queryClient.getQueryData<typeof data>(ALERTS_QUERY_KEY);
      if (previous) {
        const removed = previous.results.find((alert) => alert.key === key);
        queryClient.setQueryData(ALERTS_QUERY_KEY, {
          ...previous,
          count: Math.max(0, previous.count - 1),
          unread_count:
            removed && !removed.is_read
              ? Math.max(0, previous.unread_count - 1)
              : previous.unread_count,
          results: previous.results.filter((alert) => alert.key !== key),
        });
      }
      return { previous };
    },
    onError: (_err, _key, context) => {
      if (context?.previous) {
        queryClient.setQueryData(ALERTS_QUERY_KEY, context.previous);
      }
      toastError(t("adminPanel.alerts.dismissError"));
    },
    onSuccess: () => {
      toastSuccess(t("adminPanel.alerts.dismissSuccess"));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEY });
    },
  });

  const alerts: AdminAlert[] = data?.results ?? [];
  const unreadCount = data?.unread_count ?? 0;

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (nextOpen) {
      const unreadKeys = alerts
        .filter((alert) => !alert.is_read)
        .map((alert) => alert.key);
      if (unreadKeys.length > 0) {
        markReadMutation.mutate({ keys: unreadKeys });
      }
    }
  }

  function handleSelectAlert(alert: AdminAlert) {
    const target = rewriteAlertLink(alert.link, basePath);
    if (target) {
      setOpen(false);
      void navigate(target);
    }
  }

  return (
    <DropdownMenu.Root open={open} onOpenChange={handleOpenChange}>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                aria-label={t("adminPanel.alerts.bellLabel")}
                className="relative flex items-center justify-center rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span
                    aria-label={t("adminPanel.alerts.badgeLabel", {
                      count: unreadCount,
                    })}
                    className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[10px] font-semibold leading-none text-white"
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>
            </DropdownMenu.Trigger>
          </TooltipTrigger>
          <TooltipContent>{t("adminPanel.alerts.bellLabel")}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-50 w-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-semibold text-gray-900">
              {t("adminPanel.alerts.title")}
            </p>
            <button
              type="button"
              disabled={unreadCount === 0 || markAllReadMutation.isPending}
              onClick={() => markAllReadMutation.mutate()}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
            >
              <CheckCheck size={14} aria-hidden="true" />
              {t("adminPanel.alerts.markAllRead")}
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="flex items-center justify-center px-4 py-8">
                <p className="text-sm text-gray-400">
                  {t("adminPanel.alerts.empty")}
                </p>
              </div>
            ) : (
              alerts.map((alert) => (
                <AlertRow
                  key={alert.key}
                  alert={alert}
                  onSelect={handleSelectAlert}
                  onDismiss={(key) => dismissMutation.mutate(key)}
                />
              ))
            )}
          </div>

          <div className="border-t border-gray-100">
            <DropdownMenu.Item asChild>
              <Link
                to={`${basePath}/alerts`}
                className="block px-4 py-2.5 text-center text-sm font-medium text-blue-600 outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50"
              >
                {t("adminPanel.alerts.viewAll")}
              </Link>
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

interface AlertRowProps {
  alert: AdminAlert;
  onSelect: (alert: AdminAlert) => void;
  onDismiss: (key: string) => void;
}

function AlertRow({ alert, onSelect, onDismiss }: AlertRowProps) {
  const { t } = useTranslation();
  const hasLink = Boolean(alert.link);
  const { icon: TypeIcon, label: typeLabel } = alertTypeMeta(alert.type, t);

  return (
    <DropdownMenu.Item
      disabled={!hasLink}
      onSelect={(event) => {
        // Keep the menu mounted long enough to navigate; Radix closes it via
        // the controlled `open` state we flip in `onSelect`.
        if (!hasLink) {
          event.preventDefault();
          return;
        }
        onSelect(alert);
      }}
      className={cn(
        "flex items-start gap-2 border-b border-gray-50 px-4 py-3 outline-none last:border-0",
        hasLink
          ? "cursor-pointer hover:bg-gray-50 focus:bg-gray-50"
          : "cursor-default",
        !alert.is_read && "bg-blue-50/40",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "mt-1.5 h-2 w-2 shrink-0 rounded-full",
          SEVERITY_DOT[alert.severity],
        )}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-snug text-gray-900">
          {alert.title}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
              SEVERITY_PILL[alert.severity],
            )}
          >
            {t(`adminPanel.alerts.severity.${alert.severity}`)}
          </span>
          {typeLabel ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500">
              <TypeIcon size={12} aria-hidden="true" />
              {typeLabel}
            </span>
          ) : null}
          {alert.tenant_name ? (
            <span className="truncate text-xs text-gray-500">
              {alert.tenant_name}
            </span>
          ) : null}
          <span className="text-[11px] text-gray-400">
            {formatRelativeTime(alert.created_at)}
          </span>
        </div>
      </div>
      <button
        type="button"
        aria-label={t("adminPanel.alerts.dismiss")}
        title={t("adminPanel.alerts.dismiss")}
        onClick={(event) => {
          // Don't navigate/close the dropdown when dismissing a single row.
          event.preventDefault();
          event.stopPropagation();
          onDismiss(alert.key);
        }}
        className="-mr-1 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </DropdownMenu.Item>
  );
}
